// `usersMembers` / `groupsMembers` are OPTIONAL. When provided (e.g., from
// `view-members-projects.jsx`, which has access to a project-scoped users/groups list
// already fetched by its parent), the panel uses them directly. When not provided
// (e.g., when rendered transitively from `view-members.jsx` or `view-members-new.jsx`,
// which don't fetch that data), the panel self-fetches on mount via the NEW Triyo.API
// endpoints (`GetAllUsersNew` + `GetAllGroupsAPI`). This contract was introduced in
// TAD-5150 to remove the legacy `Plus-Icon-Panel.jsx` (which fetched via the OLD MVC
// `GetAllUsers` / `GetAllGroupsForProjectMember` / `EditProject` endpoints).
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Form, Dropdown } from "react-bootstrap";
import MemberDropdownProject from "../member-dopdown-project/member-dropdown-project";
import { EditProjectMembers } from "../../services/api/project-api";
import { GetAllUsersNew } from "../../services/api/user-api";
import { GetAllGroupsAPI } from "../../services/api/group-api";
import { PulseLoader } from "react-spinners";
import "./Plus-Icon-Panel.scss";

const PlusIconPanelNew = ({
  projects,
  onEdit,
  selectedMembers,
  userID,
  clear,
  add,
  close,
  menuWidth,
  usersMembers,
  groupsMembers,
  owners = [],
  IsMultiOwner = false,
  ownerId
}) => {
  const [payload, setPayload] = useState([]);
  const [rowFilter, setRowFilter] = useState([]);
  const [disabledUsers, setDisabledUsers] = useState(new Set());
  const [selectedRows, setSelectedRows] = useState(selectedMembers);
  const [selectedRowsCounter, setSelectedRowsCounter] = useState(selectedMembers.length ?? 0);
  const [searchFilter, setSearchFilter] = useState([]);
  const [users, setUsers] = useState(usersMembers || []);
  const [groups, setGroups] = useState(groupsMembers || []);
  const [isLoading, setIsLoading] = useState(false);
  const [selectAllStatus, setSelectAllStatus] = useState(1); //0 IS DEFAULT, 1 IS CANCEL, 2 IS CHECKED

  const ref = useRef(document.createElement("div"));

  const handleClick = (e) => {
    if (!ref.current?.contains(e.target) && !e.target?.classList.contains("plus-icon-plus-sign") && !e.target?.classList.contains("plus-icon")) {
      clearMembers();
    }
  };

  useEffect(() => {
    setIsLoading(true);
    editGroupsPayload();
    processProjectsUsersAndGroups();

    document.addEventListener("click", handleClick);
    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, []);

  useEffect(() => {
    let plusPanel = document.getElementById("plusFixed_" + projects.Id);
    plusPanel.style.cssText = `left : ${menuWidth}px !important`;
    if (menuWidth < 0) {
      let plusPanelTip = document.getElementById("plusFixedTip_" + projects.Id);
      plusPanelTip.style.cssText = `left : ${372}px !important;  transform: rotate(90deg);`;
    }
  }, []);

  const editGroupsPayload = () => {
    selectedMembers.forEach((element) => {
      if (element.GroupId && element.GroupId > 0) {
        element.UserId = element.GroupId * -1;
      }
    });
  };

  const processProjectsUsersAndGroups = async() => {
    // Resolve users/groups data. A prop counts as "provided" when it is non-null — an
    // empty array is a valid "no users/groups" state (e.g. a company with no groups) and
    // is respected as-is, so it does NOT trigger a fetch. Only an omitted prop
    // (null/undefined) triggers the self-fetch via the NEW Triyo.API endpoints (see file
    // header for the full contract).
    let resolvedUsers = usersMembers;
    let resolvedGroups = groupsMembers;

    if (resolvedUsers == null || resolvedGroups == null) {
      const searchQuery = {
        filters: [],
        pageNumber: 1,
        pageSize: 10000,
        sortBy: "name",
        sortDesc: false,
      };
      try {
        const [usersResponse, groupsResponse] = await Promise.all([
          GetAllUsersNew(searchQuery),
          GetAllGroupsAPI(searchQuery),
        ]);
        resolvedUsers = resolvedUsers ?? (usersResponse?.Users ?? []);
        resolvedGroups = resolvedGroups ?? (groupsResponse?.data?.dataList ?? []);
      } catch (error) {
        console.error("Plus-Icon-Panel-New self-fetch error:", error);
        setIsLoading(false);
        return;
      }
    }

    const tempUser = resolvedUsers;
    for (let i = 0; i < tempUser?.length; i++) {
      if (!tempUser[i].Role?.Active) setDisabledUsers((prev) => new Set(prev.add(tempUser[i].Id)));
    };

    editUsers(resolvedUsers);
    editGroups(resolvedGroups);

    setUsers(resolvedUsers);
    setGroups(resolvedGroups);

    //put selected rows on top of list
    let selectedFirst = selectedRows;

    selectedFirst.forEach((selectedMember) => {
      if (selectedMember?.GroupId && selectedMember.Group?.Members?.length > 0) {
        selectedMember.UserNameFull = `${selectedMember.UserNameFull || selectedMember.Group.Name || selectedMember.User.Name || ""} - ${selectedMember.Group.Members.length} users`;
      } else {
        selectedMember.UserNameFull = selectedMember?.User?.Name ?? "";
      }
    });

    let allRows = [...resolvedUsers, ...resolvedGroups];
    allRows = allRows.filter((row) => {
      let bool = false;
      selectedRows.forEach(
        (element) => (bool = element.UserId === row.UserId || bool)
      );
      return !bool;
    });

    selectedFirst = selectedFirst.concat(allRows);

    selectedFirst = selectedFirst.map((member) => {
      if (!member.strCompany) {
        return { ...member, strCompany:  member.CompanyName || member.User?.Company.Name || member.Group?.Members[0].User.Company.Name || "" };
      } else {
        return member;
      }
    });

    setPayload(selectedFirst);
    setIsLoading(false);
  };

  const editUsers = (users) => {
    users.forEach((user) => {
      user.UserNameFull = user.Name;
      user.IsGroup = false;
    });
  };

  const editGroups = (groups) => {
    groups.forEach((group) => {
      group.UserNameFull = group.Name + ` - ${group.Members?.length ?? 0} users`;
      group.UserId = group.Id * -1;
      group.IsGroup = true;
    });
  };

  //SEARCH BAR FUNCTIONALITY
  useEffect(() => {
    let newFilter = [];
    if (searchFilter != "") {
      console.log(payload);
      newFilter = payload.filter((row) =>
        row.UserNameFull.toLowerCase().includes(searchFilter.toLowerCase())
      );
    } else {
      newFilter = payload; //resets rows
    }
    setRowFilter(newFilter);
  }, [searchFilter]);

  useEffect(() => {
    if (searchFilter === "") {
      setRowFilter(payload);
    }
  }, [rowFilter]);

  useEffect(() => {
    setRowFilter(payload);
  }, [payload]);

  const toggleRow = (chosen, checked) => {
    let chosenRow = [...selectedRows];
    const isOwner = owners.some(
      (owner) =>
        (owner.IsGroup && chosen.IsGroup && owner.GroupId === chosen.GroupId) ||
        (!owner.IsGroup && !chosen.IsGroup && owner.UserId === chosen.UserId)
    );
    if (!checked && isOwner) {
      return; 
    }
    if (checked) {
      //HANDLES ARRAYS FOR TYPES AND FOR ADDING FILTER
      Array.isArray(chosen)
        ? chosenRow.push(...chosen)
        : chosenRow.push(chosen);
      //NEED TO REMOVE ALL DUPLICATES
      let chosenSet = new Set(chosenRow);
      chosenRow = [...chosenSet];
      if (chosenRow.length > payload.length) {
        //INSTEAD OF LOOKING FOR DUPLICATES, JUST REPLACE WITH PAYLOAD
        chosenRow = payload;
      }
    } else {
      chosenRow = [...selectedRows];
      //WHEN CHOSEN IS EMPTY
      if (chosen.length === 0) {
        chosenRow = [];
      } else {
        const index = selectedRows.findIndex(row => row.UserId === chosen.UserId);
        if (index !== -1) {
          chosenRow.splice(index, 1);
        }
      }
    }

    setSelectedRows(chosenRow);
    setSelectedRowsCounter(chosenRow.length);
    if (chosenRow.length === 0) {
      setSelectAllStatus(0);
    } else if (chosenRow.length === payload.length) {
      setSelectAllStatus(2);
    } else if (chosenRow.length > 0) {
      setSelectAllStatus(1);
    }
  };

  const clearMembers = () => {
    setSelectedRows(selectedMembers);
    setSelectedRowsCounter(0);
    close();
  };

  const addMembers = async () => {
    let newProject = projects;
    let newSelectedRows = [...selectedRows];
    newProject.Members = newSelectedRows;

    await EditProjectMembers(newProject).then(
      async (result) => {
        if (result?.response?.status === 500 || result?.status=== 500) {
          console.error("user doesn't have permission to add members");
          onEdit(true, "noPermissionToAddMembers");
          return;
        }
        const editedProjectResult = result.data;
        onEdit(false, editedProjectResult, "Members for " + editedProjectResult?.Name, " update");
      },
      (error) => {
        console.log(error);
        onEdit(true, error);
      }
    );

    setSelectedRowsCounter(0);
    close();
  };

  return (
    <React.Fragment>
      { isLoading &&
        <div className={"plus-icon-panel plusFixed"} ref={ref} id={"plusFixed_" + projects.Id}>
          <div/>
          <div style={{ "marginTop": "50%" }}>
            <PulseLoader style={{ "marginTop": "50%" }}/>
          </div>
        </div>
      }
      { !isLoading &&
        <div className={"plus-icon-panel plusFixed"} ref={ref} id={"plusFixed_" + projects.Id} >
          <div className="plus-icon-panel-dropdown-tip" id={"plusFixedTip_" + projects.Id} />
          <div className={"plus-icon-panel-top-container"}>
            <div className={"plus-icon-panel-title"}>
              Add Members/Groups
            </div>
            <hr className={"plus-icon-panel-divider"} />
            <Form.Control
              className={`plus-icon-panel-dropdown-searchbar`}
              as="textarea"
              rows="1"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              placeholder="Search for Members"
            />
          </div>
          <div className={`plus-icon-panel-dropdown-container`}>
            <Dropdown.Divider style={{ width: "478px" }} />
            { rowFilter &&
              rowFilter.map((member, index) => {
                if (disabledUsers.has(member.UserId)) return null;

                let isOwner = false;

                if (IsMultiOwner) {
                  isOwner = owners.some(owner =>
                    (owner.IsGroup && member.IsGroup && owner.GroupId === member.GroupId) || 
                    (!owner.IsGroup && !member.IsGroup && owner.UserId === member.UserId)
                  );
                } else {
                  isOwner = index === 0 && member.UserId === userID;
                }

                return (
                  <React.Fragment key={`dropdown-member-${index}`}>
                    <MemberDropdownProject
                      index={index}
                      member={member}
                      type={isOwner ? "user" : ""} 
                      filter={true}
                      toggleRow={toggleRow}
                      selectedMembers={selectedRows}
                      rowFilter={rowFilter}
                      classNameAddition={isOwner ? "-user-filter" : "-filter"}
                      ownerId={ownerId}
                      IsMultiOwner={IsMultiOwner}
                    />
                    <Dropdown.Divider style={{ width: "478px" }} />
                  </React.Fragment>
                );
              })}
          </div>
          <div className={`plus-icon-panel-button-container`}>
            <input
              type={"button"}
              variant="outline-primary"
              onClick={() => clearMembers()}
              className="plus-icon-panel-clear-button"
              value={"Cancel"}
            />
            <input
              type={"button"}
              onClick={() => addMembers()}
              value={"Add"}
              className="plus-icon-panel-apply-button"
            />
          </div>
        </div>
      }
    </React.Fragment>
  );
};

PlusIconPanelNew.propTypes = {
  projects: PropTypes.object.isRequired,
  // Optional in TAD-5150 — when omitted, the panel self-fetches via the NEW Triyo.API.
  usersMembers: PropTypes.arrayOf(PropTypes.object),
  groupsMembers: PropTypes.arrayOf(PropTypes.object),
  onEdit: PropTypes.func,
  selectedMembers: PropTypes.array.isRequired,
  userID: PropTypes.number.isRequired,
  clear: PropTypes.func,
  add: PropTypes.func,
  close: PropTypes.func,
  menuWidth: PropTypes.number,
  ownerId: PropTypes.number,
  owners: PropTypes.array,
  IsMultiOwner: PropTypes.bool
};

export default PlusIconPanelNew;
